/**
 * PortfolioDashboard — cross-client overview, sourced from the Firestore
 * googleAdsAccounts store (populated by the read-only sync).
 *
 * The store is the single source of truth shared with Andrei, so what the
 * user sees here is exactly what the agent reasons over. Reading from
 * Firestore (vs live-fetching per account) makes the page instant and
 * removes per-load API quota cost; freshness comes from the nightly sync
 * plus the manual [sync now] button.
 *
 * "Disabled" is an app-side flag (NOT a Google Ads account state): a
 * disabled account is skipped by the sync and hidden from the main list.
 * It does NOT touch the real Google Ads account. Disabled accounts are
 * managed in a collapsible section where they can be re-enabled.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, doc, query as fsQuery, where, getDocs } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../firebase';
import { authFetch } from '../../utils/cloudFunctionsAuth';
import { CF_BASE, formatCustomerId, AdsRange, MetricTotals, aggregateDaily, StoredDailyRow, monthsBetween, unpackMonthDoc, runGoogleAdsHealthCheck, runGoogleAdsAlertDetection, presetRange, todayLocal, addDaysStr } from './googleAdsApi';
import { ChangeActivityPanel } from './ChangeActivityPanel';
import { AlertRows, AlertDoc, sortAlerts, focusTabForAlert } from './AdsAlerts';
import { AdsRangeSelector } from './AdsRangeSelector';
import { PortfolioSpendChart, AccountSpend } from './PortfolioSpendChart';

// ─────────────────────────────────────────────────────────────────────────
// Types — mirror the Firestore googleAdsAccounts schema
// ─────────────────────────────────────────────────────────────────────────

interface MetricBucket {
  cost?: number;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  conversionsValue?: number;
  ctr?: number;
  avgCpc?: number;
}

interface StoredAccount {
  customerId: string;
  name?: string;
  /** Owner-set custom display label; overrides the synced `name` everywhere.
   *  Never written by the sync, so it persists. Used for accounts Google has
   *  no descriptive name for, or to give a friendlier label. */
  displayLabel?: string;
  status?: string;
  isManager?: boolean;
  testAccount?: boolean;
  disabled?: boolean;
  currencyCode?: string;
  metrics30d?: MetricBucket;
  metrics7d?: MetricBucket;
  optimizationScore?: number | null;
  optimizationScoreWeight?: number | null;
  recsCount?: number;
  syncError?: string;
  lastSyncedAt?: { seconds?: number } | null;
}

/** Display name for an account: owner label → synced name → formatted id. */
function accountLabel(a: StoredAccount): string {
  return (a.displayLabel && a.displayLabel.trim()) || a.name || formatCustomerId(a.customerId);
}
/** True when the account has a real name/label (so the id can show as a sub-line). */
function accountHasName(a: StoredAccount): boolean {
  return !!((a.displayLabel && a.displayLabel.trim()) || a.name);
}

export interface PortfolioDashboardProps {
  /** Fired when the user clicks a row — parent flips to single-account view.
   *  Optional `focus` deep-links a deep-view tab (e.g. 'recommendations'). */
  onSelectAccount: (customerId: string, focus?: string) => void;
}

type SortKey = 'name' | 'cost' | 'impressions' | 'clicks' | 'ctr' | 'conversions' | 'cpa' | 'roas';
type SortDir = 'asc' | 'desc';

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function n(v: number | undefined | null): number {
  return typeof v === 'number' && isFinite(v) ? v : 0;
}

function formatCurrency(amount: number, currency?: string): string {
  if (!currency) {
    return new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
  }
  try {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}
function formatInt(amount: number): string {
  return new Intl.NumberFormat('en-AU').format(Math.round(amount));
}
function formatPercent(ratio: number): string {
  if (!isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(2)}%`;
}
function formatRoas(cost: number, value: number): string {
  if (cost === 0 && value === 0) return '—';
  if (cost === 0 && value > 0) return '∞';
  // Spend but no conversion VALUE means value tracking isn't configured (the
  // norm for lead-gen accounts) — not a 0.00x return. Rendering a hard zero
  // read as catastrophic performance on accounts that were doing fine.
  if (value === 0) return '—';
  return `${(value / cost).toFixed(2)}x`;
}
/** Tooltip for the ROAS cell, so the '—' above is self-explaining. */
function roasTitle(cost: number, value: number): string | undefined {
  if (cost > 0 && value === 0) return 'No conversion value configured on this account — ROAS can\'t be computed. Judge on cost per conversion instead.';
  return undefined;
}

/** Cost per conversion. '—' when there are no conversions to divide by. */
function formatCpa(cost: number, conversions: number, currency?: string): string {
  if (!(conversions > 0)) return '—';
  return formatCurrency(cost / conversions, currency);
}

function relTime(seconds?: number): string {
  if (!seconds) return 'never';
  const ms = Date.now() - seconds * 1000;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

type Status = 'healthy' | 'attention' | 'alert' | 'idle' | 'error' | 'stale' | 'suspended';

/** The account sync runs nightly, so a gap beyond ~36h means a run was missed
 *  and every metric on the row is frozen at an older reading. */
const STALE_SYNC_MS = 36 * 60 * 60 * 1000;

const PROBLEMS_FIRST_KEY = 'gads.portfolio.problemsFirst';

function syncAgeMs(a: StoredAccount): number | null {
  const secs = a.lastSyncedAt?.seconds;
  return secs ? Date.now() - secs * 1000 : null;
}

function computeStatus(a: StoredAccount, m: MetricBucket, m7: MetricBucket | undefined): Status {
  if (a.syncError) return 'error';

  // STRUCTURAL problems are checked BEFORE any metric, because a dead account
  // produces innocent-looking metrics: an account Google suspended, or one
  // whose data froze days ago, both read as a plausible 'idle' row and used to
  // sort BELOW healthy accounts under "problems first" — the exact failure the
  // daily check exists to catch.
  const gStatus = String(a.status || '').toUpperCase();
  if (gStatus && gStatus !== 'ENABLED') return 'suspended';
  const age = syncAgeMs(a);
  if (age !== null && age > STALE_SYNC_MS) return 'stale';

  const cost = n(m.cost);
  if (cost === 0) return 'idle';
  // Spending with nothing to show for it across the trailing 7 days — the
  // loudest signal there is. m7 is a fixed 7-day snapshot written by the sync,
  // independent of the selected range, so it is valid in EVERY view (it used to
  // be suppressed outside the 30d view, which made this tier unreachable by
  // default).
  if (m7 && n(m7.cost) > 0 && n(m7.conversions) === 0) return 'alert';
  if (n(m.conversions) === 0) return 'attention';
  // ROAS only means something where conversion VALUE is tracked. Lead-gen
  // accounts (form fills, calls) legitimately convert with zero value, and
  // judging them on ROAS pinned them amber permanently.
  if (n(m.conversionsValue) > 0 && n(m.conversionsValue) / cost < 1) return 'attention';
  return 'healthy';
}

/** What window each status tier actually reflects — surfaced as the pill's
 *  tooltip so a 7-day 'alert' beside a range-scoped 'attention' isn't confusing. */
const STATUS_LABELS: Record<Status, { label: string; toneClass: string; hint: string }> = {
  healthy:   { label: 'healthy',   toneClass: 'healthy',   hint: 'Converting over the selected range.' },
  attention: { label: 'attention', toneClass: 'attention', hint: 'Selected range: no conversions, or return below spend.' },
  alert:     { label: 'alert',     toneClass: 'alert',     hint: 'Last 7 days: spending with zero conversions.' },
  idle:      { label: 'idle',      toneClass: 'idle',      hint: 'No spend in the selected range.' },
  error:     { label: 'error',     toneClass: 'alert',     hint: 'The last sync of this account failed.' },
  stale:     { label: 'stale',     toneClass: 'alert',     hint: 'Not synced in over 36h — these numbers are frozen, not current.' },
  suspended: { label: 'not serving', toneClass: 'alert',   hint: 'Google reports this account as not enabled (suspended, cancelled or closed).' },
};

// Concern ranking for the "problems first" sort (higher floats to the top).
// Structural failures outrank every performance read.
const STATUS_RANK: Record<Status, number> = {
  error: 6, suspended: 5, stale: 4, alert: 3, attention: 2, healthy: 1, idle: 0,
};

/** Tiny inline 7-day cost sparkline (no chart instance — one <svg> per row). */
const Sparkline: React.FC<{ values: number[]; w?: number; h?: number }> = ({ values, w = 58, h = 16 }) => {
  if (!values.length) return null;
  const max = Math.max(...values, 0);
  const n2 = values.length;
  // Flat line at the baseline when there's no spend at all.
  const pts = values.map((v, i) => {
    const x = n2 === 1 ? 0 : (i / (n2 - 1)) * (w - 2) + 1;
    const y = max > 0 ? h - 1 - (v / max) * (h - 3) : h - 1;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const lastUp = values[n2 - 1] >= (values[n2 - 2] ?? values[n2 - 1]);
  return (
    <svg width={w} height={h} className="ads-portfolio__spark" aria-hidden="true" style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={lastUp ? '#4ec07c' : '#6a9ad4'} strokeWidth={1.25} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

/** ▲/▼ spend-direction chip (neutral palette — spend direction isn't inherently
 *  good or bad; this is a "moving more/less than usual" signal).
 *
 *  Compares YESTERDAY against the prior-week daily average — both complete
 *  days. It used to compare today (a partial day, however many hours in) against
 *  a full yesterday, so every account read ▼70-90% all morning and the chip was
 *  noise until late evening. This matches the alert engine's own convention of
 *  treating today as partial/volatile. */
const DeltaChip: React.FC<{ yesterday: number; baseline: number }> = ({ yesterday, baseline }) => {
  if (yesterday === 0 && baseline === 0) return null;
  if (baseline === 0) {
    return <span className="ads-portfolio__delta ads-portfolio__delta--up" title="Yesterday: first spend in this window">▲ new</span>;
  }
  const pct = (yesterday - baseline) / baseline;
  const tip = `Yesterday vs the prior-week daily average (today is still partial, so it's excluded)`;
  if (Math.abs(pct) < 0.005) return <span className="ads-portfolio__delta ads-portfolio__delta--flat" title={tip}>≈</span>;
  const up = pct > 0;
  return (
    <span className={`ads-portfolio__delta ads-portfolio__delta--${up ? 'up' : 'down'}`} title={tip}>
      {up ? '▲' : '▼'}{Math.abs(Math.round(pct * 100))}%
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────

export const PortfolioDashboard: React.FC<PortfolioDashboardProps> = ({ onSelectAccount }) => {
  const [accounts, setAccounts] = useState<StoredAccount[]>([]);
  /** Per-account metrics aggregated from the daily store for a non-30d range
   *  (empty in the 30d view, which uses the metrics30d snapshot). */
  const [rangeMetrics, setRangeMetrics] = useState<Record<string, MetricTotals>>({});
  const [loaded, setLoaded] = useState(false);
  /** Set when the Firestore subscription errors (permissions / network) —
   *  distinct from a genuinely-empty store, so we don't tell the user to
   *  "sync now" when the real problem is the read failed. */
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<{ lastRunAtMs?: number; synced?: number; errored?: number } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [health, setHealth] = useState<{ lastRunAtMs?: number; summary?: { totalQueries?: number; totalFailures?: number; failingQueries?: Array<{ cid: string; name: string; key: string; errorCode: string; error: string }> } } | null>(null);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [alerts, setAlerts] = useState<AlertDoc[]>([]);
  const [detectingAlerts, setDetectingAlerts] = useState(false);
  /** Alerts collapse to a one-line summary strip; expand for full triage rows. */
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [showDisabled, setShowDisabled] = useState(false);
  const [togglingCid, setTogglingCid] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('cost');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  // "Problems first" floats error/stale/suspended/alerting accounts to the top
  // (over the active metric sort). ON by default and remembered: this view's job
  // is the daily "what needs me?" read, and burying that behind a toggle the
  // user had to re-tick every visit defeated it. Compact tightens row density.
  const [problemsFirst, setProblemsFirst] = useState<boolean>(() => {
    try { return window.localStorage.getItem(PROBLEMS_FIRST_KEY) !== '0'; } catch { return true; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(PROBLEMS_FIRST_KEY, problemsFirst ? '1' : '0'); } catch { /* storage blocked */ }
  }, [problemsFirst]);
  const [compact, setCompact] = useState(false);

  // The portfolio timeframe — drives BOTH the spend chart AND the accounts table
  // (one shared selector next to the chart). Defaults to last 3 days: a single
  // 'today' point isn't a trend (the daily store has no intraday data).
  const [chartRange, setChartRange] = useState<AdsRange>(() => presetRange('3d'));
  // 30d reads the instant metrics30d snapshot; every other range aggregates each
  // account's daily store over the window.
  const isRangeMode = chartRange.preset !== '30d';
  // Per-account daily rows over a window covering the chart range AND the last 7
  // days — one bounded read per account feeds the spend chart, the per-row
  // sparkline, and the vs-yesterday deltas.
  const [dailyByAccount, setDailyByAccount] = useState<Record<string, StoredDailyRow[]>>({});

  // ── Live subscription to the store ─────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(
      fsQuery(collection(db, 'googleAdsAccounts')),
      (snap) => {
        const list: StoredAccount[] = snap.docs.map((d) => ({ customerId: d.id, ...(d.data() as any) }));
        setAccounts(list);
        setLoadError(null);
        setLoaded(true);
      },
      (err) => {
        console.error('[PortfolioDashboard] store subscription error:', err);
        setLoadError(err?.message || 'Could not load accounts');
        setLoaded(true);
      },
    );
    return () => unsub();
  }, []);

  // ── Sync-status doc (for "last synced X ago") ──────────────────────
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'google_ads_sync_status'), (snap) => {
      setSyncStatus(snap.exists() ? (snap.data() as any) : null);
    });
    return () => unsub();
  }, []);

  // ── Query health-check doc (validates every GAQL the app depends on) ─
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'google_ads_health'), (snap) => {
      setHealth(snap.exists() ? (snap.data() as any) : null);
    }, () => { /* best-effort */ });
    return () => unsub();
  }, []);

  const runHealth = useCallback(async () => {
    setCheckingHealth(true);
    try {
      const r = await runGoogleAdsHealthCheck();
      if (!r.ok) throw new Error(r.error || 'health check failed');
      const fails = r.summary?.totalFailures || 0;
      if (fails === 0) toast.success(`All ${r.summary?.totalQueries || 0} query checks healthy.`);
      else toast.error(`${fails} query check${fails === 1 ? '' : 's'} failing — see the health indicator.`);
    } catch (e: any) {
      toast.error(`Health check failed: ${e?.message || 'unknown error'}`);
    } finally {
      setCheckingHealth(false);
    }
  }, []);

  // ── Proactive alerts (change-detection feed) ───────────────────────
  useEffect(() => {
    const unsub = onSnapshot(
      fsQuery(collection(db, 'googleAdsAlerts'), where('status', 'in', ['active', 'acknowledged'])),
      (snap) => setAlerts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      (err) => { console.warn('[PortfolioDashboard] alerts sub error:', err); },
    );
    return () => unsub();
  }, []);

  const detectAlerts = useCallback(async () => {
    setDetectingAlerts(true);
    try {
      const r = await runGoogleAdsAlertDetection();
      if (!r.ok) throw new Error(r.error || 'detection failed');
      toast.success(`Alert check done — ${r.active || 0} active across ${r.accounts || 0} account${r.accounts === 1 ? '' : 's'}.`);
    } catch (e: any) {
      toast.error(`Alert check failed: ${e?.message || 'unknown error'}`);
    } finally {
      setDetectingAlerts(false);
    }
  }, []);

  const sortedAlerts = useMemo(() => sortAlerts(alerts), [alerts]);
  const activeAlertCount = useMemo(() => alerts.filter((a) => a.status === 'active').length, [alerts]);

  /** Active-alert severity tallies for the summary strip chips. */
  const sevCounts = useMemo(() => {
    const c = { high: 0, medium: 0, low: 0 };
    for (const a of alerts) {
      if (a.status !== 'active') continue;
      const s = (a.severity === 'high' || a.severity === 'low') ? a.severity : 'medium';
      c[s]++;
    }
    return c;
  }, [alerts]);

  /** Per-account ACTIVE alert rollup for the table-row chips. */
  const alertsByAccount = useMemo(() => {
    const map = new Map<string, { total: number; high: number; medium: number; low: number; titles: string[] }>();
    for (const a of alerts) {
      if (a.status !== 'active' || !a.accountId) continue;
      const e = map.get(a.accountId) || { total: 0, high: 0, medium: 0, low: 0, titles: [] };
      const s = (a.severity === 'high' || a.severity === 'low') ? a.severity : 'medium';
      e.total++; e[s]++;
      if (a.title) e.titles.push(`${s}: ${a.title}`);
      map.set(a.accountId, e);
    }
    return map;
  }, [alerts]);

  // ── Sync now ───────────────────────────────────────────────────────
  const syncNow = useCallback(async () => {
    setSyncing(true);
    try {
      const r = await authFetch(`${CF_BASE}/syncGoogleAdsNow`, { method: 'POST' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      toast.success(`Synced ${data.synced} account${data.synced === 1 ? '' : 's'}${data.errored ? `, ${data.errored} errored` : ''}.`);
      // The onSnapshot subscription refreshes the table automatically.
    } catch (e: any) {
      toast.error(`Sync failed: ${e?.message || 'unknown error'}`);
    } finally {
      setSyncing(false);
    }
  }, []);

  // ── Disable / enable an account ────────────────────────────────────
  const setDisabled = useCallback(async (customerId: string, disabled: boolean) => {
    setTogglingCid(customerId);
    try {
      const r = await authFetch(`${CF_BASE}/setGoogleAdsAccountDisabled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, disabled }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      toast.success(disabled ? 'Account disabled (hidden from sync + listings)' : 'Account re-enabled');
      // onSnapshot updates the lists.
    } catch (e: any) {
      toast.error(`Could not update account: ${e?.message || 'unknown error'}`);
    } finally {
      setTogglingCid(null);
    }
  }, []);

  // Set or clear a custom display label for an account (some accounts have no
  // Google descriptive_name to sync). Blank clears it → falls back to id.
  const setLabel = useCallback(async (a: StoredAccount) => {
    const current = (a.displayLabel && a.displayLabel.trim()) || a.name || '';
    const next = window.prompt(`Display label for ${formatCustomerId(a.customerId)} (leave blank to clear):`, current);
    if (next === null) return; // cancelled
    setTogglingCid(a.customerId);
    try {
      const r = await authFetch(`${CF_BASE}/setGoogleAdsAccountLabel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: a.customerId, label: next.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || !data?.ok) throw new Error(data?.error || `HTTP ${r.status}`);
      toast.success(next.trim() ? 'Label saved' : 'Label cleared');
      // onSnapshot refreshes the list.
    } catch (e: any) {
      toast.error(`Could not set label: ${e?.message || 'unknown error'}`);
    } finally {
      setTogglingCid(null);
    }
  }, []);

  // ── Partition: active (non-manager, non-disabled) vs disabled ──────
  const activeAccounts = useMemo(
    () => accounts.filter((a) => !a.isManager && a.disabled !== true),
    [accounts],
  );
  const disabledAccounts = useMemo(
    () => accounts.filter((a) => a.disabled === true),
    [accounts],
  );

  // ── Range aggregation (custom ranges) ──────────────────────────────
  // For a non-default range, aggregate each active account's dailyMetrics
  // over the window (one bounded read per account). Accounts with no stored
  // days in the window render as having no data (zeros) — NOT the metrics30d
  // snapshot, which would mislead under a different range label. The nightly
  // sync populates ~104 days through today, so the today/yesterday/7d/90d/
  // month presets are always covered; only deep custom ranges can be sparse.
  // The 30d view uses the instant metrics30d snapshot and skips this entirely.
  const rangeFrom = chartRange.from;
  const rangeTo = chartRange.to;
  // Stable key on the SET of active cids so this effect (N bounded reads)
  // doesn't re-fire on every store snapshot during a sync (which churns the
  // activeAccounts array identity without changing the cid set).
  const activeCidsKey = useMemo(
    () => activeAccounts.map((a) => a.customerId).sort().join(','),
    [activeAccounts],
  );
  useEffect(() => {
    if (!isRangeMode || !rangeFrom || !rangeTo) { setRangeMetrics({}); return; }
    const cids = activeCidsKey ? activeCidsKey.split(',') : [];
    if (cids.length === 0) { setRangeMetrics({}); return; }
    let cancelled = false;
    (async () => {
      const map: Record<string, MetricTotals> = {};
      const months = monthsBetween(rangeFrom, rangeTo);
      if (months.length === 0) { if (!cancelled) setRangeMetrics({}); return; }
      await Promise.all(cids.map(async (cid) => {
        try {
          // Packed store: query the account-tier month docs spanning the
          // window, then unpack + clamp to [rangeFrom,rangeTo] before summing.
          const snap = await getDocs(fsQuery(
            collection(db, 'googleAdsAccounts', cid, 'dailyMetrics'),
            where('month', '>=', months[0]),
            where('month', '<=', months[months.length - 1]),
          ));
          const rows: StoredDailyRow[] = snap.docs.flatMap((d) => unpackMonthDoc(d.data(), rangeFrom, rangeTo)) as any;
          if (rows.length > 0) map[cid] = aggregateDaily(rows);
        } catch (e) {
          console.warn('[PortfolioDashboard] range aggregate failed', cid, e);
        }
      }));
      if (!cancelled) setRangeMetrics(map);
    })();
    return () => { cancelled = true; };
  }, [isRangeMode, rangeFrom, rangeTo, activeCidsKey]);

  // ── Per-account daily rows (spend chart + sparklines + deltas) ─────
  // Window = the chart range UNION the last 7 days (sparkline) UNION yesterday
  // (delta). One bounded read per active account; clamped per-feature on use.
  const chartFrom = chartRange.from;
  const chartTo = chartRange.to;
  useEffect(() => {
    const cids = activeCidsKey ? activeCidsKey.split(',') : [];
    if (cids.length === 0) { setDailyByAccount({}); return; }
    const today = todayLocal();
    const from = [chartFrom, addDaysStr(today, -7)].sort()[0];   // earliest needed
    const to = chartTo > today ? chartTo : today;
    let cancelled = false;
    (async () => {
      const map: Record<string, StoredDailyRow[]> = {};
      const months = monthsBetween(from, to);
      if (months.length === 0) { if (!cancelled) setDailyByAccount({}); return; }
      await Promise.all(cids.map(async (cid) => {
        try {
          const snap = await getDocs(fsQuery(
            collection(db, 'googleAdsAccounts', cid, 'dailyMetrics'),
            where('month', '>=', months[0]),
            where('month', '<=', months[months.length - 1]),
          ));
          map[cid] = snap.docs.flatMap((d) => unpackMonthDoc(d.data(), from, to)) as any;
        } catch (e) {
          console.warn('[PortfolioDashboard] daily rows fetch failed', cid, e);
        }
      }));
      if (!cancelled) setDailyByAccount(map);
    })();
    return () => { cancelled = true; };
  }, [chartFrom, chartTo, activeCidsKey]);

  /** The metric bucket to display for an account given the active range. */
  const metricsFor = useCallback((a: StoredAccount): MetricBucket => {
    if (isRangeMode) return (rangeMetrics[a.customerId] as MetricBucket) || {};
    return a.metrics30d || {};
  }, [isRangeMode, rangeMetrics]);

  const setSort = useCallback((key: SortKey) => {
    let nextKey: SortKey; let nextDir: SortDir;
    if (sortKey === key) { nextKey = sortKey; nextDir = sortDir === 'asc' ? 'desc' : 'asc'; }
    else { nextKey = key; nextDir = key === 'name' ? 'asc' : 'desc'; }
    setSortKey(nextKey); setSortDir(nextDir);
  }, [sortKey, sortDir]);

  const sortedActive = useMemo(() => {
    const mul = sortDir === 'asc' ? 1 : -1;
    const noData = sortDir === 'asc' ? Infinity : -Infinity;
    const get = (a: StoredAccount): number | string => {
      const m = metricsFor(a);
      switch (sortKey) {
        case 'name': return accountLabel(a).toLowerCase();
        case 'cost': return n(m.cost);
        case 'impressions': return n(m.impressions);
        case 'clicks': return n(m.clicks);
        case 'ctr': return n(m.impressions) > 0 ? n(m.clicks) / n(m.impressions) : noData;
        case 'conversions': return n(m.conversions);
        // Lower CPA is better, so an account with no conversions sorts to the
        // far end rather than looking like a $0 winner.
        case 'cpa': return n(m.conversions) > 0 ? n(m.cost) / n(m.conversions) : noData;
        case 'roas': return n(m.cost) > 0 ? n(m.conversionsValue) / n(m.cost) : noData;
      }
    };
    return [...activeAccounts].sort((a, b) => {
      const va = get(a); const vb = get(b);
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * mul;
      return ((va as number) - (vb as number)) * mul;
    });
  }, [activeAccounts, sortKey, sortDir, metricsFor]);

  // ── Totals ─────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let cost = 0, impressions = 0, clicks = 0, conversions = 0, conversionsValue = 0;
    const currencies = new Set<string>();
    for (const a of activeAccounts) {
      const m = metricsFor(a);
      cost += n(m.cost); impressions += n(m.impressions); clicks += n(m.clicks);
      conversions += n(m.conversions); conversionsValue += n(m.conversionsValue);
      if (a.currencyCode) currencies.add(a.currencyCode);
    }
    return {
      cost, impressions, clicks, conversions, conversionsValue,
      currency: currencies.size === 1 ? Array.from(currencies)[0] : undefined,
      currencyMixed: currencies.size > 1,
    };
  }, [activeAccounts, metricsFor]);

  // ── Spend chart series — one per active account, rows clamped to chartRange.
  // Sorted by customerId so each account keeps a STABLE colour regardless of the
  // table's sort order.
  const chartData = useMemo<AccountSpend[]>(() => {
    const from = chartRange.from, to = chartRange.to;
    return [...activeAccounts]
      .sort((a, b) => a.customerId.localeCompare(b.customerId))
      .map((a) => ({
        accountId: a.customerId,
        label: accountLabel(a),
        rows: (dailyByAccount[a.customerId] || [])
          .filter((r) => r.date && r.date >= from && r.date <= to)
          .map((r) => ({ date: r.date as string, cost: n(r.cost) })),
      }));
  }, [activeAccounts, dailyByAccount, chartRange]);

  // ── Today-vs-yesterday spend + 7-day sparkline (from the daily rows) ──
  const todayStr = todayLocal();
  const yesterdayStr = addDaysStr(todayStr, -1);
  const last7 = useMemo(() => {
    const out: string[] = [];
    for (let i = 6; i >= 0; i--) out.push(addDaysStr(todayStr, -i));
    return out;
  }, [todayStr]);
  /** The 7 complete days BEFORE yesterday — the delta chip's baseline. Today is
   *  excluded everywhere here because it's a partial day. */
  const priorWeek = useMemo(() => {
    const out: string[] = [];
    for (let i = 8; i >= 2; i--) out.push(addDaysStr(todayStr, -i));
    return out;
  }, [todayStr]);
  /** Per-account yesterday cost, its prior-week daily baseline, today's partial
   *  spend, and the last-7-day cost series for the sparkline. */
  const dailyDerived = useMemo(() => {
    const map: Record<string, { today: number; yesterday: number; baseline: number; spark: number[] }> = {};
    for (const cid of Object.keys(dailyByAccount)) {
      const byDate = new Map((dailyByAccount[cid] || []).map((r) => [r.date, n(r.cost)]));
      const priorVals = priorWeek.map((d) => byDate.get(d) || 0);
      const priorDays = priorVals.length || 1;
      map[cid] = {
        today: byDate.get(todayStr) || 0,
        yesterday: byDate.get(yesterdayStr) || 0,
        baseline: priorVals.reduce((s, v) => s + v, 0) / priorDays,
        spark: last7.map((d) => byDate.get(d) || 0),
      };
    }
    return map;
  }, [dailyByAccount, todayStr, yesterdayStr, last7, priorWeek]);
  /** Portfolio-wide yesterday vs baseline spend (for the totals row delta). */
  const portfolioToday = useMemo(() => {
    let t = 0, y = 0, b = 0;
    for (const a of activeAccounts) {
      const d = dailyDerived[a.customerId];
      if (d) { t += d.today; y += d.yesterday; b += d.baseline; }
    }
    return { today: t, yesterday: y, baseline: b };
  }, [activeAccounts, dailyDerived]);

  // ── "Problems first" ordering (over the active sort) ──────────────
  const displayAccounts = useMemo(() => {
    if (!problemsFirst) return sortedActive;
    const concern = (a: StoredAccount): number => {
      // metrics7d is a fixed trailing-7d snapshot, valid in every view — passing
      // it unconditionally is what makes the 'alert' tier reachable at all.
      const st = computeStatus(a, metricsFor(a), a.metrics7d);
      const ac = alertsByAccount.get(a.customerId);
      const alertScore = ac ? ac.high * 10 + ac.medium * 3 + ac.low : 0;
      return STATUS_RANK[st] * 100 + alertScore;
    };
    // Stable: re-sort the already-sorted list by concern desc (ties keep the
    // active metric order).
    return [...sortedActive].sort((a, b) => concern(b) - concern(a));
  }, [problemsFirst, sortedActive, metricsFor, isRangeMode, alertsByAccount]);

  const lastSyncedSeconds = syncStatus?.lastRunAtMs ? Math.floor(syncStatus.lastRunAtMs / 1000) : undefined;
  /** Accounts whose own last sync is stale — surfaced in the header so a frozen
   *  account is countable at a glance, not just discoverable row by row. */
  const staleCount = useMemo(
    () => activeAccounts.filter((a) => { const age = syncAgeMs(a); return age !== null && age > STALE_SYNC_MS; }).length,
    [activeAccounts],
  );

  // ── Auto-sync when stale (freshness for all-day monitoring) ───────
  // Re-sync when the data is >20 min old AND the tab is focused — fresh
  // without hammering the API quota. Never auto-syncs a never-synced store.
  useEffect(() => {
    const STALE_MS = 20 * 60 * 1000;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      const last = syncStatus?.lastRunAtMs;
      if (!last || syncing) return;
      if (Date.now() - last > STALE_MS) void syncNow();
    };
    const id = window.setInterval(tick, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', tick);
    tick();
    return () => { window.clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, [syncStatus?.lastRunAtMs, syncing, syncNow]);

  // ── Render ─────────────────────────────────────────────────────────
  if (!loaded) {
    return <div className="text-muted text-center py-10">Loading accounts…</div>;
  }

  const neverSynced = accounts.length === 0;

  return (
    <section className="ads-portfolio">
      <header className="ads-portfolio__head">
        <div className="ads-portfolio__head-left">
          <h2 className="ads-portfolio__title">All accounts · {chartRange.label}</h2>
          <div className="ads-portfolio__subtitle">
            {activeAccounts.length} active{disabledAccounts.length > 0 ? ` · ${disabledAccounts.length} disabled` : ''}
            {' · '}synced {relTime(lastSyncedSeconds)}
            {/* The sync writes a per-run failure count; it was read into state
                and then never shown, so a half-failed sync looked complete. */}
            {!!syncStatus?.errored && (
              <span className="ads-portfolio__health ads-portfolio__health--bad" title={`${syncStatus.errored} account${syncStatus.errored === 1 ? '' : 's'} failed to sync on the last run — their numbers are stale.`}>
                {' · '}⚠ {syncStatus.errored} failed
              </span>
            )}
            {staleCount > 0 && (
              <span className="ads-portfolio__health ads-portfolio__health--bad" title="Accounts whose last successful sync is over 36h old — their rows show frozen numbers.">
                {' · '}⚠ {staleCount} stale
              </span>
            )}
            {health?.summary && (
              (() => {
                const fails = health.summary.totalFailures || 0;
                const total = health.summary.totalQueries || 0;
                const tip = fails === 0
                  ? `All ${total} Google Ads query checks healthy (last run ${relTime(health.lastRunAtMs ? Math.floor(health.lastRunAtMs / 1000) : undefined)}).`
                  : `Failing query checks:\n` + (health.summary.failingQueries || []).map((f) => `• ${f.name || f.cid} — ${f.key}: ${f.errorCode || f.error}`).join('\n');
                return (
                  <span className={`ads-portfolio__health ads-portfolio__health--${fails === 0 ? 'ok' : 'bad'}`} title={tip}>
                    {' · '}{fails === 0 ? `✓ ${total} checks ok` : `⚠ ${fails} query check${fails === 1 ? '' : 's'} failing`}
                  </span>
                );
              })()
            )}
          </div>
        </div>
        <div className="ads-portfolio__head-right">
          {disabledAccounts.length > 0 && (
            <button
              type="button"
              className="ads-portfolio__reload"
              onClick={() => setShowDisabled((v) => !v)}
              title="Manage disabled accounts"
            >
              {showDisabled ? '[hide disabled]' : `[disabled (${disabledAccounts.length})]`}
            </button>
          )}
          <button
            type="button"
            className="ads-portfolio__reload"
            onClick={detectAlerts}
            disabled={detectingAlerts}
            title="Run change-detection now (spend/conversion anomalies, disapprovals, budget-capped, waste). Read-only; nightly otherwise."
          >
            {detectingAlerts ? '[checking…]' : '[check alerts]'}
          </button>
          <button
            type="button"
            className="ads-portfolio__reload"
            onClick={runHealth}
            disabled={checkingHealth}
            title="Validate every Google Ads query the app depends on, across all accounts (read-only)"
          >
            {checkingHealth ? '[checking…]' : '[health check]'}
          </button>
          <button
            type="button"
            className="ads-portfolio__reload"
            onClick={syncNow}
            disabled={syncing}
            title="Re-fetch all account data from Google Ads now"
          >
            {syncing ? '[syncing…]' : '[sync now]'}
          </button>
        </div>
      </header>

      {/* Alerts collapse to a one-line summary strip — per-account severity
          chips live on the table rows below, and the account page carries the
          full actionable list. Expand here for quick cross-account triage. */}
      {sortedAlerts.length > 0 && (
        <div className="ads-portfolio__alerts">
          <button
            type="button"
            className="ads-portfolio__alerts-head ads-portfolio__alerts-head--toggle"
            onClick={() => setAlertsExpanded((v) => !v)}
            aria-expanded={alertsExpanded}
            title={alertsExpanded ? 'Collapse alert details' : 'Expand alert details'}
          >
            <span>
              ⚠ {activeAlertCount} active alert{activeAlertCount === 1 ? '' : 's'}
              {sortedAlerts.length > activeAlertCount ? ` · ${sortedAlerts.length - activeAlertCount} acknowledged` : ''}
            </span>
            {sevCounts.high > 0 && <span className="ads-portfolio__sevchip ads-portfolio__sevchip--high">{sevCounts.high} high</span>}
            {sevCounts.medium > 0 && <span className="ads-portfolio__sevchip ads-portfolio__sevchip--medium">{sevCounts.medium} medium</span>}
            {sevCounts.low > 0 && <span className="ads-portfolio__sevchip ads-portfolio__sevchip--low">{sevCounts.low} low</span>}
            <span className="ads-portfolio__alerts-caret">{alertsExpanded ? '▾ hide' : '▸ details'}</span>
          </button>
          {/* onAct makes the row itself clickable: it opens the account AND
              carries the alert type as a deep-view focus, so you land on the tab
              that answers it. Without this the portfolio's alert rows were inert
              — the one place cross-account triage actually starts. */}
          {alertsExpanded && (
            <AlertRows
              alerts={sortedAlerts}
              onSelectAccount={onSelectAccount}
              onAct={(a) => { if (a.accountId) onSelectAccount(a.accountId, focusTabForAlert(a.type)); }}
            />
          )}
        </div>
      )}

      {loadError ? (
        <div className="gads-alert gads-alert--err">
          ✗ Couldn't load accounts: {loadError} — check your connection / permissions and refresh.
        </div>
      ) : neverSynced ? (
        <div className="gads-alert gads-alert--info">
          ℹ No account data yet. Hit <strong>[sync now]</strong> to pull your accounts from Google Ads.
          After the first sync, data refreshes automatically each morning.
        </div>
      ) : (
        <>
          {/* Portfolio spend chart — one line per active account, own range
              (default last 3 days) so the table can stay on the page range. */}
          <div style={{ border: '1px solid #242424', borderRadius: 6, padding: '10px 12px 4px', marginBottom: 14, background: 'rgba(255,255,255,0.012)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
              <span style={{ color: '#8bbf8b', fontWeight: 600, fontSize: 13 }}>spend by account · {chartRange.label}</span>
              <AdsRangeSelector value={chartRange} onChange={setChartRange} />
            </div>
            <PortfolioSpendChart data={chartData} from={chartRange.from} to={chartRange.to} currency={totals.currency} />
          </div>

          {/* Table controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="ads-portfolio__reload"
              onClick={() => setProblemsFirst((v) => !v)}
              title="Float accounts with errors / attention / active alerts to the top (over the column sort)"
            >
              {problemsFirst ? '[✓ problems first]' : '[problems first]'}
            </button>
            <button
              type="button"
              className="ads-portfolio__reload"
              onClick={() => setCompact((v) => !v)}
              title="Tighter rows — fit more accounts without scrolling"
            >
              {compact ? '[✓ compact]' : '[compact]'}
            </button>
          </div>

          {/* Active accounts table */}
          <div className="ads-portfolio__table-wrap">
            <table className={'ads-portfolio__table' + (compact ? ' ads-portfolio__table--compact' : '')}>
              <thead>
                <tr>
                  <SortHeader label="Account" k="name" sortKey={sortKey} sortDir={sortDir} onSort={setSort} />
                  <SortHeader label="Cost" k="cost" sortKey={sortKey} sortDir={sortDir} onSort={setSort} num />
                  <SortHeader label="Impressions" k="impressions" sortKey={sortKey} sortDir={sortDir} onSort={setSort} num />
                  <SortHeader label="Clicks" k="clicks" sortKey={sortKey} sortDir={sortDir} onSort={setSort} num />
                  <SortHeader label="CTR" k="ctr" sortKey={sortKey} sortDir={sortDir} onSort={setSort} num />
                  <SortHeader label="Conv." k="conversions" sortKey={sortKey} sortDir={sortDir} onSort={setSort} num />
                  <SortHeader label="Cost / conv." k="cpa" sortKey={sortKey} sortDir={sortDir} onSort={setSort} num />
                  <SortHeader label="ROAS" k="roas" sortKey={sortKey} sortDir={sortDir} onSort={setSort} num />
                  <th className="ads-portfolio__th-status">Status</th>
                  <th></th>
                </tr>
                <tr className="ads-portfolio__totals">
                  <th>
                    Portfolio totals
                    {totals.currencyMixed && (
                      <span className="ads-portfolio__mixed-badge" title="Accounts span multiple currencies — totals are nominal sums">
                        mixed currencies
                      </span>
                    )}
                    {(portfolioToday.today > 0 || portfolioToday.yesterday > 0) && (
                      <div className="ads-portfolio__today ads-portfolio__today--totals">
                        <span title="Today is still in progress — the trend chip below compares complete days only">
                          today {formatCurrency(portfolioToday.today, totals.currency)} <span style={{ opacity: 0.6 }}>· partial</span>
                        </span>
                        <DeltaChip yesterday={portfolioToday.yesterday} baseline={portfolioToday.baseline} />
                      </div>
                    )}
                  </th>
                  <td className="num" data-label="Cost">{formatCurrency(totals.cost, totals.currency)}</td>
                  <td className="num" data-label="Impr">{formatInt(totals.impressions)}</td>
                  <td className="num" data-label="Clicks">{formatInt(totals.clicks)}</td>
                  <td className="num" data-label="CTR">{formatPercent(totals.impressions > 0 ? totals.clicks / totals.impressions : 0)}</td>
                  <td className="num" data-label="Conv">{totals.conversions.toFixed(2)}</td>
                  <td className="num" data-label="Cost/conv">{formatCpa(totals.cost, totals.conversions, totals.currency)}</td>
                  <td className="num" data-label="ROAS" title={roasTitle(totals.cost, totals.conversionsValue)}>{formatRoas(totals.cost, totals.conversionsValue)}</td>
                  <td></td>
                  <td></td>
                </tr>
              </thead>
              <tbody>
                {displayAccounts.length === 0 ? (
                  <tr><td colSpan={10} className="text-muted" style={{ padding: '14px 12px' }}>No active accounts.</td></tr>
                ) : displayAccounts.map((a) => {
                  const m = metricsFor(a);
                  const cost = n(m.cost), impr = n(m.impressions), clicks = n(m.clicks);
                  const ctr = impr > 0 ? clicks / impr : 0;
                  // metrics7d passed unconditionally — it's a fixed trailing-7d
                  // snapshot, not range-scoped, so the 'alert' tier applies in
                  // every view rather than only the 30d one.
                  const status = computeStatus(a, m, a.metrics7d);
                  return (
                    // Row is clickable for mouse convenience but is NOT a
                    // button (it contains the focusable disable button, so
                    // role=button would be invalid nested-interactive ARIA).
                    // The keyboard / screen-reader affordance lives on the
                    // account-name button inside the first cell.
                    <tr
                      key={a.customerId}
                      className="ads-portfolio__row"
                      onClick={() => onSelectAccount(a.customerId)}
                    >
                      <td className="ads-portfolio__name">
                        <button
                          type="button"
                          className="ads-portfolio__name-btn"
                          onClick={(e) => { e.stopPropagation(); onSelectAccount(a.customerId); }}
                          title={`Open ${accountLabel(a)}`}
                        >
                          <span className="ads-portfolio__name-text">{accountLabel(a)}</span>
                          {accountHasName(a) && <span className="ads-portfolio__name-cid">{formatCustomerId(a.customerId)}</span>}
                        </button>
                        {(a.optimizationScore != null || a.recsCount) && (
                          <div className="ads-portfolio__optline">
                            {a.optimizationScore != null && (() => {
                              const pct = a.optimizationScore <= 1 ? Math.round(a.optimizationScore * 100) : Math.round(a.optimizationScore);
                              const band = pct >= 80 ? 'ok' : pct >= 70 ? 'mid' : 'low';
                              return <span className={`ads-portfolio__opt ads-portfolio__opt--${band}`} title="Google optimization score">opt {pct}%</span>;
                            })()}
                            {!!a.recsCount && (
                              <button
                                type="button"
                                className="ads-portfolio__recs ads-portfolio__recs--link"
                                title="Open Google recommendations for this account (apply / dismiss)"
                                onClick={(e) => { e.stopPropagation(); onSelectAccount(a.customerId, 'recommendations'); }}
                              >
                                {a.recsCount} rec{a.recsCount === 1 ? '' : 's'} ›
                              </button>
                            )}
                          </div>
                        )}
                        {/* Today's pace — 7-day sparkline + today spend + vs-yesterday delta. */}
                        {(() => {
                          const d = dailyDerived[a.customerId];
                          if (!d) return null;
                          return (
                            <div className="ads-portfolio__today">
                              <Sparkline values={d.spark} />
                              <span className="ads-portfolio__today-amt" title="Today's spend so far (account timezone) — a partial day, which is why the trend chip beside it compares complete days only">today {formatCurrency(d.today, a.currencyCode)} <span style={{ opacity: 0.6 }}>· partial</span></span>
                              <DeltaChip yesterday={d.yesterday} baseline={d.baseline} />
                            </div>
                          );
                        })()}
                      </td>
                      {a.syncError ? (
                        <td colSpan={7} className="ads-portfolio__row-error">
                          <i className="bi bi-exclamation-triangle me-1"></i>{a.syncError}
                        </td>
                      ) : (
                        <>
                          <td className="num" data-label="Cost">{formatCurrency(cost, a.currencyCode)}</td>
                          <td className="num" data-label="Impr">{formatInt(impr)}</td>
                          <td className="num" data-label="Clicks">{formatInt(clicks)}</td>
                          <td className="num" data-label="CTR">{formatPercent(ctr)}</td>
                          <td className="num" data-label="Conv">{n(m.conversions).toFixed(2)}</td>
                          <td className="num" data-label="Cost/conv">{formatCpa(cost, n(m.conversions), a.currencyCode)}</td>
                          <td className="num" data-label="ROAS" title={roasTitle(cost, n(m.conversionsValue))}>{formatRoas(cost, n(m.conversionsValue))}</td>
                        </>
                      )}
                      <td className="ads-portfolio__status">
                        <StatusPill status={status} />
                        {(() => {
                          const ac = alertsByAccount.get(a.customerId);
                          if (!ac || ac.total === 0) return null;
                          const worst = ac.high > 0 ? 'high' : ac.medium > 0 ? 'medium' : 'low';
                          return (
                            <span
                              className={`ads-portfolio__alertchip ads-portfolio__alertchip--${worst}`}
                              title={ac.titles.join('\n')}
                            >
                              ⚠{ac.total}
                            </span>
                          );
                        })()}
                      </td>
                      <td
                        className="ads-portfolio__status"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="ads-portfolio__rowbtn"
                          disabled={togglingCid === a.customerId}
                          onClick={() => setLabel(a)}
                          title="Set a custom display label for this account (useful when Google has no account name)"
                        >
                          label
                        </button>
                        <button
                          type="button"
                          className="ads-portfolio__rowbtn"
                          disabled={togglingCid === a.customerId}
                          onClick={() => setDisabled(a.customerId, true)}
                          title="Disable — stop syncing and hide this account (does NOT affect the real Google Ads account)"
                        >
                          {togglingCid === a.customerId ? '…' : 'disable'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Disabled accounts management */}
          {showDisabled && disabledAccounts.length > 0 && (
            <div className="ads-portfolio__disabled">
              <h3 className="ads-portfolio__disabled-title">Disabled accounts</h3>
              <p className="ads-portfolio__disabled-note">
                These are excluded from syncing and the main list. Disabling is app-side only — it doesn't change
                anything in Google Ads. Re-enable to start syncing again.
              </p>
              <ul className="ads-portfolio__disabled-list">
                {disabledAccounts.map((a) => (
                  <li key={a.customerId} className="ads-portfolio__disabled-row">
                    <span className="ads-portfolio__disabled-name">
                      {accountLabel(a)}
                      <span className="ads-portfolio__disabled-cid"> · {formatCustomerId(a.customerId)}</span>
                      {a.isManager && <span className="ads-portfolio__disabled-cid"> · manager</span>}
                    </span>
                    <button
                      type="button"
                      className="ads-portfolio__rowbtn"
                      disabled={togglingCid === a.customerId}
                      onClick={() => setDisabled(a.customerId, false)}
                    >
                      {togglingCid === a.customerId ? '…' : 're-enable'}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Global proposals inbox — pending/in-flight/failed across ALL
          accounts (so a draft for an account you never open still gets
          seen) + the master kill switch. Applied/undone history lives on
          each account's page in the same panel, account-scoped. */}
      <ChangeActivityPanel onSelectAccount={onSelectAccount} />

      {/* Agent Access moved to an owner-only header icon (it spans Google Ads +
          CRM/billing/email now, so it's no longer Google-Ads-page-specific). */}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

const SortHeader: React.FC<{
  label: string; k: SortKey; sortKey: SortKey; sortDir: SortDir; onSort: (k: SortKey) => void; num?: boolean;
}> = ({ label, k, sortKey, sortDir, onSort, num }) => {
  const active = sortKey === k;
  const arrow = active ? (sortDir === 'asc' ? '▲' : '▼') : '';
  return (
    <th
      className={`ads-portfolio__th${num ? ' num' : ''}${active ? ' active' : ''}`}
      onClick={() => onSort(k)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSort(k); } }}
    >
      {label} {arrow}
    </th>
  );
};

const StatusPill: React.FC<{ status: Status }> = ({ status }) => {
  const cfg = STATUS_LABELS[status];
  // Tooltip explains WHICH WINDOW the tier reflects — it used to just repeat the
  // label, which was useless next to a 7-day 'alert' beside a range-scoped
  // 'attention'.
  return (
    <span className={`ads-portfolio__pill ads-portfolio__pill--${cfg.toneClass}`} title={cfg.hint}>
      ● {cfg.label}
    </span>
  );
};

export default PortfolioDashboard;
